import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D8fromD1 = () => {
  const title = "Calculation of D8 (Ashtamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D8 chart, or Ashtamsa chart, is used in Vedic astrology to analyze
        longevity, inheritance, and the occult. Below is the step-by-step
        process to calculate the D8 chart from the D1 (Rasi) chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D8 (Ashtamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Saturn is located at 21° Aquarius, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Eight Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into eight equal parts, each spanning 3.75°.
        To find the Ashtamsa position of a planet, we divide its degree in the
        sign by 3.75.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Ashtamsa \ Position = \frac{Degree \ in \ Sign}{3.75}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{3.75} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 3.75°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{3.75} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 3.75°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{3.75} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 3.75°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`3 \leq \frac{Degree \ in \ Sign}{3.75} < 4`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>fourth 3.75°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`4 \leq \frac{Degree \ in \ Sign}{3.75} < 5`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>fifth 3.75°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`5 \leq \frac{Degree \ in \ Sign}{3.75} < 6`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>sixth 3.75°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`6 \leq \frac{Degree \ in \ Sign}{3.75} < 7`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>seventh 3.75°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`7 \leq \frac{Degree \ in \ Sign}{3.75} < 8`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>eighth 3.75°</strong>.
        </Typography>
      </Box>

      <Typography variant="h5" gutterBottom>
        3. Assign the Ashtamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 3.75°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 3.75°:</strong> The planet is assigned to the{" "}
        <strong>8th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 3.75°:</strong> The planet is assigned to the{" "}
        <strong>6th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 3.75°:</strong> The planet is assigned to the{" "}
        <strong>3rd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifth 3.75°:</strong> The planet is assigned to the{" "}
        <strong>5th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixth 3.75°:</strong> The planet is assigned to the{" "}
        <strong>10th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Seventh 3.75°:</strong> The planet is assigned to the{" "}
        <strong>2nd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eighth 3.75°:</strong> The planet is assigned to the{" "}
        <strong>7th sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Saturn at 21° Aquarius
      </Typography>

      <Typography variant="body1" paragraph>
        Aquarius is the D1 sign. Saturn at 21° falls in the sixth 3.75° (between
        18.75° and 22.5°). Therefore, it belongs to the{" "}
        <strong>10th sign</strong> from Aquarius, which is{" "}
        <strong>Scorpio</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D8 (Ashtamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D8
        (Ashtamsa) position from the D1 (Rasi) chart, using the planet's degree
        and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_ashtamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Ashtamsa the planet is in based on its degree
    if degree <= 3.75:
        # First 3.75° belongs to the same sign
        ashtamsa_sign = zodiac_signs[sign_index]
    elif 3.75 < degree <= 7.5:
        # Second 3.75° belongs to the 8th sign from the current sign
        ashtamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 7.5 < degree <= 11.25:
        # Third 3.75° belongs to the 6th sign from the current sign
        ashtamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 11.25 < degree <= 15:
        # Fourth 3.75° belongs to the 3rd sign from the current sign
        ashtamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 15 < degree <= 18.75:
        # Fifth 3.75° belongs to the 5th sign from the current sign
        ashtamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 18.75 < degree <= 22.5:
        # Sixth 3.75° belongs to the 10th sign from the current sign
        ashtamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 22.5 < degree <= 26.25:
        # Seventh 3.75° belongs to the 2nd sign from the current sign
        ashtamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    else:
        # Eighth 3.75° belongs to the 7th sign from the current sign
        ashtamsa_sign = zodiac_signs[(sign_index + 6) % 12]

    return ashtamsa_sign

# Example usage
planet = "Saturn"
degree = 21
sign = "Aquarius"

ashtamsa_sign = calculate_ashtamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {ashtamsa_sign} in the D8 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Ashtamsa for a planet based on
        its degree and sign in the D1 chart. The program divides the sign into
        eight equal parts and assigns the Ashtamsa based on the planet's degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into eight parts: the first 3.75°, the
        second 3.75°, and so on. It assigns the Ashtamsa based on the planet's
        degree within the sign:
        <br />
        - First 3.75° belongs to the same sign.
        <br />
        - Second 3.75° belongs to the 8th sign from the D1 sign.
        <br />
        - Third 3.75° belongs to the 6th sign from the D1 sign.
        <br />
        - Fourth 3.75° belongs to the 3rd sign from the D1 sign.
        <br />
        - Fifth 3.75° belongs to the 5th sign from the D1 sign.
        <br />
        - Sixth 3.75° belongs to the 10th sign from the D1 sign.
        <br />
        - Seventh 3.75° belongs to the 2nd sign from the D1 sign.
        <br />- Eighth 3.75° belongs to the 7th sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D8fromD1;
